// @ts-strict-ignore
import {
  Container,
  FilterMenuCheckbox,
  FilterMenuRadio,
  FilterShowMoreButton,
  LoadingBar,
  OldText,
  theme,
} from '@gassan-ui'
import {
  ArticleAttributeFilter,
  ArticleSortEnum,
  BrandCollectionFilter,
  BrandTypeFilter,
} from '@generated'
import { useTranslation } from 'next-i18next'
import { rem } from 'polished'
import React, { FC, useState } from 'react'
import { CombinedError } from 'urql'
import { FilterAction, FilterState } from '../reducer'
import { IsolatedRolexLink } from './IsolatedRolexLink'
import { getFilteredAttributes } from './helpers/get-filtered-attributes'

type Props = Omit<FilterState, 'page'> & {
  isRolexRelevantPage?: boolean
  isLoading: boolean
  areProductsLoading: boolean
  attributes: ArticleAttributeFilter[]
  brandOptions: BrandTypeFilter[]
  collectionOptions: BrandCollectionFilter[]
  error?: CombinedError
  totalProducts?: number
  dispatch: React.Dispatch<FilterAction>
  isPreOwned: boolean
}

const FilterBar: FC<Props> = ({
  isRolexRelevantPage = false,
  isLoading,
  areProductsLoading,
  attributes,
  brandOptions,
  collectionOptions,
  brands,
  collections,
  error,
  dispatch,
  totalProducts,
  filters,
  isPreOwned,
  sort,
}) => {
  const priceCategory = attributes.length
    ? attributes.filter((item) => item.attributeName === 'priceCategory')[0]
    : null
  const preOwnedCollection = attributes.length
    ? attributes.filter((item) => item.attributeName === 'Collection')[0]
    : null
  const [showAll, setShowAll] = useState(false)
  const {
    t,
    i18n: { language },
  } = useTranslation(['global', 'filters', 'other'])
  const filteredAttributes = getFilteredAttributes(attributes, ['Collection'])

  return (
    <Container
      pt={['2rem']}
      display={{ _: 'none', large: 'flex' }}
      borderTop={`1px solid ${theme.colors.shades[200]}`}
      alignItems="center"
      flexWrap="wrap"
    >
      {isLoading && attributes.length === 0 ? (
        [...Array(6)].map((u, i) => (
          <LoadingBar key={`loading-${i}`} my="0.5rem" height={rem(26)} width="6rem" mr="2rem" />
        ))
      ) : error ? null : (
        <>
          {areProductsLoading ? (
            <LoadingBar mb="1rem" height={rem(26)} width="5rem" mr="2rem" />
          ) : (
            <OldText color="shades.500" mb="1rem" mr="2.25rem" minWidth="5rem">
              {totalProducts &&
                `${totalProducts} ${t(totalProducts > 1 ? 'products' : 'product', {
                  ns: 'global',
                })}`}
            </OldText>
          )}
          <FilterMenuRadio
            items={[
              {
                label: t('popularity', { ns: 'filters' }),
                value: ArticleSortEnum.Populair,
              },
              {
                label: t('newestFirst', { ns: 'filters' }),
                value: ArticleSortEnum.New,
              },
              {
                label: t('priceASC', { ns: 'filters' }),
                value: ArticleSortEnum.PriceAsc,
              },
              {
                label: t('priceDESC', { ns: 'filters' }),
                value: ArticleSortEnum.PriceDesc,
              },
            ]}
            label={t('sort', { ns: 'filters' })}
            value={sort}
            onChange={(sort) => dispatch({ type: 'set-sort', sort })}
          />
          {brandOptions.length > 0 && (
            <FilterMenuCheckbox
              isolatedFirstElement={isRolexRelevantPage && <IsolatedRolexLink variant="regular" />}
              items={brandOptions.map((brand) => ({
                label: brand.brand,
                value: brand.merk_nr,
              }))}
              label={t('brands', { ns: 'filters' })}
              activeFilters={brands.length}
              lang={language}
              value={brands}
              onClickReset={() => dispatch({ type: 'reset-brands' })}
              onSelectItem={(value) =>
                dispatch({
                  type: 'set-brands',
                  value: value as number[],
                })
              }
            />
          )}
          {collectionOptions.length > 0 && (
            <FilterMenuCheckbox
              isolatedFirstElement={
                isRolexRelevantPage && isPreOwned && <IsolatedRolexLink variant="pre-owned" />
              }
              items={collectionOptions.map((collection) => ({
                label: `${collection.collection}`,
                value: collection.collectionNumber,
              }))}
              label={t(isPreOwned ? 'brands' : 'collection', { ns: 'filters' })}
              activeFilters={collections.length}
              lang={language}
              value={collections}
              onClickReset={() => dispatch({ type: 'reset-collections' })}
              onSelectItem={(value) =>
                dispatch({
                  type: 'set-collections',
                  value: value as number[],
                })
              }
            />
          )}
          {preOwnedCollection && (
            <FilterMenuCheckbox
              items={preOwnedCollection.articleFilterAttributeValues.map((item) => ({
                label: item.attributeValue,
                value: item.attributeValueID,
              }))}
              label={t('collection', { ns: 'filters' })}
              activeFilters={
                filters[preOwnedCollection.attributeID] &&
                filters[preOwnedCollection.attributeID].length
              }
              lang={language}
              value={
                filters[preOwnedCollection.attributeID]
                  ? filters[preOwnedCollection.attributeID]
                  : []
              }
              onClickReset={() =>
                dispatch({ type: 'reset-filter', key: preOwnedCollection.attributeID })
              }
              onSelectItem={(value) =>
                dispatch({
                  type: 'set-filter',
                  key: preOwnedCollection.attributeID,
                  value: value as number[],
                })
              }
            />
          )}
          {priceCategory && (
            <FilterMenuCheckbox
              items={priceCategory.articleFilterAttributeValues.map((item) => ({
                label: item.attributeValue,
                value: item.attributeValueID,
              }))}
              label={t('price', { ns: 'filters' })}
              activeFilters={
                filters[priceCategory.attributeID] && filters[priceCategory.attributeID].length
              }
              lang={language}
              value={filters[priceCategory.attributeID] ? filters[priceCategory.attributeID] : []}
              onClickReset={() =>
                dispatch({ type: 'reset-filter', key: priceCategory.attributeID })
              }
              onSelectItem={(value) =>
                dispatch({
                  type: 'set-filter',
                  key: priceCategory.attributeID,
                  value: value as number[],
                })
              }
            />
          )}
          {filteredAttributes.slice(0, !showAll ? 5 : 99).map((filter) => (
            <FilterMenuCheckbox
              key={filter.attributeID}
              items={filter.articleFilterAttributeValues.map((item) => ({
                label: item.attributeValue,
                value: item.attributeValueID,
              }))}
              activeFilters={filters[filter.attributeID] && filters[filter.attributeID].length}
              label={filter.attributeName}
              value={filters[filter.attributeID] ? filters[filter.attributeID] : []}
              onSelectItem={(value) =>
                dispatch({
                  type: 'set-filter',
                  key: filter.attributeID,
                  value: value as number[],
                })
              }
              lang={language}
              onClickReset={() => dispatch({ type: 'reset-filter', key: filter.attributeID })}
            ></FilterMenuCheckbox>
          ))}
          {!showAll && filteredAttributes.length > 5 && (
            <FilterShowMoreButton onClick={() => setShowAll(true)}>
              {t('allFilters', { ns: 'other' })}
            </FilterShowMoreButton>
          )}
        </>
      )}
    </Container>
  )
}

export default FilterBar
